import type { ApiFactory, ApiRef, ConfigApi } from '@backstage/frontend-plugin-api';
import type { AnyRouter } from '@trpc/server';
import type { TRPCOptionsProxy } from '@trpc/tanstack-react-query';

import {
  ApiBlueprint,
  configApiRef,
  createExtensionBlueprint,
  createExtensionBlueprintParams,
  fetchApiRef,
  microsoftAuthApiRef,
} from '@backstage/frontend-plugin-api';
import { createTRPCClient, httpBatchLink, httpLink } from '@trpc/client';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';

import { queryClient } from '../queryClient';

type MicrosoftAuthApi = typeof microsoftAuthApiRef extends ApiRef<infer TApi, string> ? TApi : never;

type TrpcApiBlueprintParams<TApi extends AnyRouter> = {
  apiRef: ApiRef<TRPCOptionsProxy<TApi>>;
  path: string;
} & (
  | {
    getHeaders: (options: TrpcApiHeadersOptions) => Promise<TrpcRequestHeaders> | TrpcRequestHeaders;
    useBatch?: false;
  }
  | { getHeaders?: undefined; useBatch?: boolean }
);

type TrpcApiHeadersOptions = {
  api: MicrosoftAuthApi;
  config: ConfigApi;
  context: Record<string, unknown>;
};

type TrpcRequestHeaders = Record<string, string | string[] | undefined>;

const apiFactory = <
  TApi,
  TImpl extends TApi,
  TDeps extends Record<string, unknown>,
>(api: ApiFactory<TApi, TImpl, TDeps>) => api;

export const TrpcApiBlueprint = createExtensionBlueprint({
  attachTo: { id: 'root', input: 'apis' },
  defineParams<TApi extends AnyRouter>(params: TrpcApiBlueprintParams<TApi>) {
    return createExtensionBlueprintParams(params);
  },
  * factory(params) {
    yield ApiBlueprint.dataRefs.factory(
      apiFactory({
        api: params.apiRef,
        deps: {
          config: configApiRef,
          fetchApi: fetchApiRef,
          microsoftAuthApi: microsoftAuthApiRef,
        },
        factory: ({ config, fetchApi, microsoftAuthApi }) => {
          const url = `${config.getString('backend.baseUrl').replace(/\/+$/, '')}/api/${params.path}`;
          const headerCallback = params.getHeaders;
          const getHeaders = headerCallback
            ? (context: Record<string, unknown>) => headerCallback({ api: microsoftAuthApi, config, context })
            : undefined;
          const useBatch = !getHeaders && params.useBatch !== false;
          const clientLink = useBatch
            ? httpBatchLink({ fetch: fetchApi.fetch, url })
            : httpLink({
                fetch: fetchApi.fetch,
                headers: ({ op }) => getHeaders?.(op.context) ?? {},
                url,
              });

          return createTRPCOptionsProxy({
            client: createTRPCClient({ links: [clientLink] }),
            queryClient,
          });
        },
      }),
    );
  },
  kind: 'api',
  output: [ApiBlueprint.dataRefs.factory],
});

export type { TRPCOptionsProxy } from '@trpc/tanstack-react-query';
